import type { AppConfig } from '../config';
import type { MachineConfig } from '../domain/machine';
import { ACCELERATED_TIME_SCALE, REAL_TIME_SCALE, TIMING } from '../domain/timing';
import { ApiClient } from '../services/api';
import { WebAudioFeedback, type AudioFeedback } from '../services/audio';
import { DemoAuthService, HttpAuthService } from '../services/auth';
import { demoCatalog, HttpCatalogService } from '../services/catalog';
import { DirectRobotGateway } from '../services/direct-robot';
import { IdbStore, MemoryStore } from '../services/local-store';
import { localAddresses, nativeHttp } from '../services/native';
import { OfflineAuthService } from '../services/offline-auth';
import { OfflineCatalogService } from '../services/offline-catalog';
import { HttpRobotGateway, SimulatedRobotGateway } from '../services/robot';
import { SessionState } from '../services/session';
import { SyncService } from '../services/sync';
import { TabletSetupService } from '../services/tablet-setup';
import { UpdateCheck } from '../services/update-check';
import { createEffectRunner, type Services } from './effects';
import { MachineStore } from './store';

export function createMachineConfig(config: AppConfig): MachineConfig {
  return {
    timeScale: config.timeAccelerated ? ACCELERATED_TIME_SCALE : REAL_TIME_SCALE,
    estopRequiresConfirmation: config.estopRequiresConfirmation,
    countdownSeconds: TIMING.countdown,
    cycleSeconds: TIMING.cycle,
  };
}

/** Real backend when VITE_API_URL is set; otherwise fully simulated so the UI runs offline. */
export function createServices(config: AppConfig, debug: boolean): Services {
  const audio = new WebAudioFeedback(config.soundEnabled);
  if (config.apiUrl !== null && config.control === 'direct') {
    return createDirectServices(config.apiUrl, config, audio);
  }
  if (config.apiUrl !== null) {
    const client = new ApiClient({ baseUrl: config.apiUrl });
    return {
      audio,
      auth: new HttpAuthService(client, config.unitId),
      catalog: new HttpCatalogService(client, config.unitId),
      robot: new HttpRobotGateway(client, config.unitId),
    };
  }
  return {
    audio,
    auth: new DemoAuthService(config.demoPin, config.demoOperatorName),
    catalog: demoCatalog,
    robot: new SimulatedRobotGateway(
      debug ? (message, detail) => console.info(`[robot] ${message}`, detail) : undefined,
    ),
  };
}

/**
 * The tablet drives the robot's switch over Wi-Fi. One build serves every robot: which one this
 * tablet drives is chosen when it is set up (see TabletSetupService). It keeps every sign-in and cycle in its own
 * database, so it works the same with or without data; the server is caught up whenever there is.
 */
function createDirectServices(apiUrl: string, config: AppConfig, audio: AudioFeedback): Services {
  const store = typeof indexedDB === 'undefined' ? new MemoryStore() : new IdbStore();
  // Sign-in falls back to the offline check quickly when there is no data.
  const sessionClient = new ApiClient({ baseUrl: apiUrl, timeoutMs: 4000 });
  const tabletClient = new ApiClient({ baseUrl: apiUrl, authScheme: 'Tablet' });
  const session = new SessionState();
  const sync = new SyncService(store, tabletClient);
  const robot = new DirectRobotGateway({
    store,
    sync,
    session,
    transport: nativeHttp,
    localAddresses,
  });
  sync.start();
  const updates = config.siteUrl
    ? new UpdateCheck({ siteUrl: config.siteUrl, appVersion: config.appVersion })
    : undefined;
  updates?.start();
  return {
    audio,
    auth: new OfflineAuthService({
      client: sessionClient,
      sync,
      store,
      session,
    }),
    catalog: new OfflineCatalogService(sync, store, robot),
    robot,
    sync,
    setup: new TabletSetupService({
      client: new ApiClient({ baseUrl: apiUrl, timeoutMs: 10_000 }),
      sync,
      store,
      robot,
      appVersion: config.appVersion,
    }),
    ...(updates ? { updates } : {}),
  };
}

/** Wires config, services and the state machine together. */
export function createApp(config: AppConfig, services: Services) {
  const store = new MachineStore(createMachineConfig(config), createEffectRunner(services));
  // The server ended our cycle (machine switched off outside Evesix, relay failsafe, failed power
  // check): stop showing "light is on" and tell the operator why.
  services.robot.onCycleLost((reason) => store.dispatch({ type: 'CYCLE_LOST', reason }));
  return { store, services };
}
